import React from "react"
import { Alert, FormGroup, Input, Label } from "reactstrap"
import { groupsText, qualifiersPerGroupRange, qualifiersRule, tenis10PhaseShape } from "../tenis10Phase"

// A Tenis10 phase asks how many children go through from each group, as tenis10.ro did (P51 canvas, Tudor 2026-10-25: variant B);
// the total, the draw and the small final follow from it.

/** The phase's qualifiers per group from the field's value, or null while the value is not a valid count. */
export const perGroupOf = (value, context) => {
  const { min, max } = qualifiersPerGroupRange(context)
  const perGroup = Number(value)
  return value !== "" && Number.isInteger(perGroup) && perGroup >= min && perGroup <= max ? perGroup : null
}

/** The field's value a saved phase starts with: a quota that is not N of every group (set up before the rule) starts empty. */
export const initialValue = (phase, context) => phase?.mlNumber == null || phase.mlNumber % context.numberOfGroups !== 0
  ? "" : String(phase.mlNumber / context.numberOfGroups)

function Summary({ perGroup, context }) {
  const shape = tenis10PhaseShape(perGroup, context.numberOfGroups)
  return <Alert color="info" className="tenis10-phase-summary">
    <strong>{shape.quota} qualified children · {shape.drawSize} draw places · {shape.byes} BYEs</strong>
    <ul className="mb-0 mt-2 pl-3">
      <li>Qualifiers: the {qualifiersRule(perGroup)} ({groupsText(context.numberOfGroups)}), by the Tenis10 group standings. No inter-group ranking.</li>
      <li>{shape.fixedBracket
        ? <>Bracket: the tenis10.ro fixed bracket for {shape.quota} from {groupsText(context.numberOfGroups)} — the group winners head the draw and take the BYEs.</>
        : <>Bracket: the Sportya draw — the BYEs go to the group winners and children of one group never meet in the first round.</>}</li>
      {shape.smallFinal && <li>Small final: the two semifinal losers play for 3rd place; places 1–4 come from the results.</li>}
    </ul>
  </Alert>
}

export default function Tenis10PhaseFields({ value, onChange, invalid, context, phase }) {
  const { min, max } = qualifiersPerGroupRange(context)
  const perGroup = perGroupOf(value, context)
  const legacy = phase?.mlNumber != null && phase.mlNumber % context.numberOfGroups !== 0
  return <>
    {legacy && <Alert color="warning">This phase admits {phase.mlNumber} qualifiers, not the same number from every group. A Tenis10 phase takes the first children of every group: choose how many before generating it.</Alert>}
    <FormGroup>
      <Label for="phase-quota">Qualified children from each group *</Label>
      <Input id="phase-quota" type="number" min={min} max={max} step="1" value={value} invalid={invalid}
        aria-describedby="phase-quota-help" onChange={event => onChange(event.target.value)} />
      <small id="phase-quota-help" className="form-text text-muted">{context.numberOfGroups === 1
        ? `Between ${min} and ${max} children of the group: tenis10.ro has a bracket for 2, 3 or 4 children of one group.`
        : `Between ${min} and ${max} from each of the ${groupsText(context.numberOfGroups)}.`}</small>
      {invalid && <div className="invalid-feedback d-block">Enter a whole number between {min} and {max}.</div>}
    </FormGroup>
    {perGroup != null && <Summary perGroup={perGroup} context={context} />}
  </>
}
